import React, { useEffect, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ShoppingCart, Trash2, Plus, Minus, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable cart drawer used across /order (wholesale), /shop/:slug (retail portal), /shop (public).
 * Reads items + pricing context from CartContext. On checkout, POSTs to /api/checkout/create
 * (server-side price resolution + stock validation) and redirects to the Stripe hosted page.
 */
export default function CartDrawer({
  clientEmailPrefill = "",
  clientNamePrefill = "",
  clientId = null,
  portalSlug = null,
  source = "direct_site",
  readOnly = false,
  readOnlyLabel = "Read-only",
  triggerClassName = "bg-[var(--brand)] hover:bg-[#8d1820] text-white border-[var(--brand)] shadow-[0_3px_12px_rgba(176,30,36,0.28)] font-bold",
  badgeClassName = "bg-white text-[var(--brand)] border-2 border-[var(--brand)]",
}) {
  const cart = useCart();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(clientEmailPrefill);
  const [name, setName] = useState(clientNamePrefill);
  const [busy, setBusy] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountBusy, setDiscountBusy] = useState(false);
  const [tipSelection, setTipSelection] = useState("none");
  const [customTip, setCustomTip] = useState("");

  useEffect(() => { setAppliedDiscount(null); }, [cart.items, cart.context]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) { toast.error("Enter a discount code"); return; }
    if (!cart.items.length) { toast.error("Add an item before applying a code"); return; }
    setDiscountBusy(true);
    try {
      const { data } = await api.post("/discounts/preview", {
        code: discountCode,
        items: cart.items.map((it) => ({ product_id: it.product_id, qty: it.qty })),
        pricing_context: cart.context,
        client_id: clientId,
        portal_slug: portalSlug,
      });
      setAppliedDiscount(data.discount);
      setDiscountCode(data.discount.code);
      toast.success(`Code ${data.discount.code} applied`);
    } catch (err) {
      setAppliedDiscount(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "That discount code could not be applied");
    } finally { setDiscountBusy(false); }
  };

  const discountAmount = Number(appliedDiscount?.amount_applied || 0);
  const discountedSubtotal = Math.max(0, cart.total - discountAmount);
  const presetTipPercent = ["10", "15", "20"].includes(tipSelection) ? Number(tipSelection) : 0;
  const tipAmount = tipSelection === "custom"
    ? Math.max(0, Number(customTip || 0))
    : Number((discountedSubtotal * presetTipPercent / 100).toFixed(2));
  const checkoutTotal = discountedSubtotal + tipAmount;

  const checkout = async () => {
    if (!cart.items.length) { toast.error("Cart is empty"); return; }
    if (!email) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/create", {
        items: cart.items.map((it) => ({ product_id: it.product_id, qty: it.qty })),
        pricing_context: cart.context,
        source,
        client_email: email,
        client_name: name,
        client_id: clientId,
        portal_slug: portalSlug,
        discount_code: appliedDiscount?.code || null,
        tip_selection: tipSelection,
        custom_tip_amount: tipSelection === "custom" ? tipAmount : null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  if (cart.readOnly || readOnly) {
    return <span className="text-xs font-medium text-slate-500" data-testid="cart-ordering-read-only">{readOnlyLabel}</span>;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        className={`relative ${triggerClassName}`}
        type="button"
        onPointerDown={() => setOpen(true)}
        onClick={() => setOpen(true)}
        data-testid="cart-open-button"
      >
        <ShoppingCart size={16} className="mr-1" aria-hidden="true" />
        Cart
        {cart.count > 0 && (
          <span
            className={`absolute -top-2 -right-2 ${badgeClassName} text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center`}
            data-testid="cart-count-badge"
          >
            {cart.count}
          </span>
        )}
      </Button>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="border-b border-[var(--border)] pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={16} /> Your Cart
            <span className="ml-auto text-xs uppercase tracking-wider text-gray-500 font-normal">
              {cart.context} pricing
            </span>
          </SheetTitle>
        </SheetHeader>

        {/* Line items */}
        <div className="flex-1 overflow-y-auto py-3 space-y-2" data-testid="cart-items">
          {cart.items.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-16">Your cart is empty.</p>
          )}
          {cart.items.map((it) => {
            const unit = cart.price(it);
            return (
              <div
                key={it.product_id}
                className="flex gap-3 p-2 rounded-lg hover:bg-[var(--bg-soft)]"
                data-testid={`cart-item-${it.product_id}`}
              >
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-14 h-14 object-cover rounded-md" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-gray-100" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="capitalize font-medium text-sm truncate">{it.name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{it.sku}</div>
                  {it.bundle_name && (
                    <div
                      className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-800"
                      data-testid={`cart-bundle-tag-${it.product_id}`}
                      title="Part of Dynamic Bundle — priced accordingly"
                    >
                      ⋆ {it.bundle_name}
                    </div>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <button
                      onClick={() => cart.setQty(it.product_id, it.qty - 1)}
                      className="w-6 h-6 rounded border border-[var(--border)] hover:bg-white flex items-center justify-center"
                      data-testid={`cart-decrement-${it.product_id}`}
                    ><Minus size={11} /></button>
                    <span className="w-8 text-center text-sm tabular-nums" data-testid={`cart-qty-${it.product_id}`}>{it.qty}</span>
                    <button
                      onClick={() => cart.setQty(it.product_id, Math.min(it.qty + 1, it.warehouse_stock))}
                      className="w-6 h-6 rounded border border-[var(--border)] hover:bg-white flex items-center justify-center"
                      data-testid={`cart-increment-${it.product_id}`}
                    ><Plus size={11} /></button>
                    <button
                      onClick={() => cart.remove(it.product_id)}
                      className="ml-1 text-red-500 hover:text-red-700"
                      data-testid={`cart-remove-${it.product_id}`}
                    ><Trash2 size={12} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">${(unit * it.qty).toFixed(2)}</div>
                  <div className="text-[10px] text-gray-500">${unit.toFixed(2)} ea</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border)] pt-4 space-y-3">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-gray-600" data-testid="cart-subtotal">
              <span>Subtotal</span><span className="tabular-nums">${cart.total.toFixed(2)}</span>
            </div>
            {appliedDiscount && (
              <div className="flex justify-between text-emerald-700 font-medium" data-testid="cart-discount-line">
                <span>Discount · {appliedDiscount.code}</span>
                <span className="tabular-nums">−${discountAmount.toFixed(2)}</span>
              </div>
            )}
            {tipAmount > 0 && (
              <div className="flex justify-between text-amber-800 font-medium" data-testid="cart-tip-line">
                <span>{tipSelection === "custom" ? "Tip · custom" : `Tip · ${tipSelection}%`}</span>
                <span className="tabular-nums">${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-1">
              <span>Total</span>
              <span className="tabular-nums" data-testid="cart-total">${checkoutTotal.toFixed(2)}</span>
            </div>
          </div>
          {cart.items.length > 0 && (
            <>
              <div>
                <Label className="text-[11px]">Your email *</Label>
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="cart-email"
                />
              </div>
              <div>
                <Label className="text-[11px]">Name (optional)</Label>
                <Input
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Full name" data-testid="cart-name"
                />
              </div>
              <div>
                <Label className="text-[11px]" id="cart-tip-label">Add a tip <span className="text-gray-400">(optional)</span></Label>
                <div className="grid grid-cols-5 gap-1.5 mt-1.5" role="group" aria-labelledby="cart-tip-label" data-testid="cart-tip-options">
                  {["10", "15", "20"].map((percent) => (
                    <Button key={percent} type="button" variant={tipSelection === percent ? "default" : "outline"}
                      onClick={() => setTipSelection(percent)} className={tipSelection === percent ? "btn-brand" : ""}
                      data-testid={`cart-tip-${percent}-button`}>{percent}%</Button>
                  ))}
                  <Button type="button" variant={tipSelection === "custom" ? "default" : "outline"}
                    onClick={() => setTipSelection("custom")} className={tipSelection === "custom" ? "btn-brand" : ""}
                    data-testid="cart-tip-custom-button">Custom</Button>
                  <Button type="button" variant={tipSelection === "none" ? "default" : "outline"}
                    onClick={() => { setTipSelection("none"); setCustomTip(""); }} className={tipSelection === "none" ? "btn-brand" : ""}
                    data-testid="cart-tip-none-button">No tip</Button>
                </div>
                {tipSelection === "custom" && (
                  <div className="mt-2">
                    <Label htmlFor="cart-custom-tip" className="sr-only">Custom tip amount</Label>
                    <Input id="cart-custom-tip" type="number" min="0.01" max="10000" step="0.01" value={customTip}
                      onChange={(e) => setCustomTip(e.target.value)} placeholder="Tip amount" data-testid="cart-custom-tip-input" />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11px]" htmlFor="cart-discount-code">Discount code</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="cart-discount-code"
                    value={discountCode}
                    onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setAppliedDiscount(null); }}
                    placeholder="Enter code"
                    className="font-mono uppercase"
                    data-testid="cart-discount-code-input"
                  />
                  <Button type="button" variant="outline" onClick={applyDiscount} disabled={discountBusy} data-testid="apply-cart-discount-button">
                    {discountBusy ? "…" : "Apply"}
                  </Button>
                </div>
                {appliedDiscount && (
                  <button type="button" onClick={() => { setAppliedDiscount(null); setDiscountCode(""); }} className="mt-1 text-[11px] text-gray-500 hover:text-red-600" data-testid="remove-cart-discount-button">
                    Remove code
                  </button>
                )}
              </div>
              <Button
                onClick={checkout} disabled={busy} className="w-full btn-brand h-11"
                data-testid="cart-checkout-button"
              >
                {busy ? <Loader2 className="animate-spin mr-1" size={14} />
                      : <Lock size={14} className="mr-1.5" />}
                {busy ? "Redirecting…" : `Checkout · $${checkoutTotal.toFixed(2)}`}
              </Button>
              <p className="text-[10px] text-center text-gray-400">
                Secure payment by Stripe · Test card 4242 4242 4242 4242
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
